import React, { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { AppPasswordInput, BrandLogo, OutlineButton, PrimaryButton } from '../../../components/ui';
import {
  requestPasswordOtp,
  resetPasswordWithOtp,
} from '../../../repositories/auth.repository';
import {
  STAFF_PASSWORD_MIN_LENGTH,
  validateStaffPassword,
} from '../../../utils/staffPassword';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(String(location.state?.email || '').trim());
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sendOtp = async () => {
    const dest = email.trim();
    if (!dest) {
      setError('Email is required');
      return false;
    }
    await requestPasswordOtp(dest);
    return true;
  };

  const handleEmail = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const ok = await sendOtp();
      if (ok) setStep('reset');
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSubmitting(true);
    try {
      await sendOtp();
    } catch (err) {
      setError(err?.message || 'Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (event) => {
    event.preventDefault();
    setError('');
    const next = validateStaffPassword(password, confirmPassword);
    if (!/^\d{6}$/.test(otp.trim())) {
      next.otp = 'Enter the 6-digit OTP';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    try {
      await resetPasswordWithOtp(email.trim(), otp.trim(), password);
      navigate('/auth/sign-in', {
        replace: true,
        state: { notice: 'Password updated. Sign in with your new password.' },
      });
    } catch (err) {
      setError(err?.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box as="form" onSubmit={step === 'email' ? handleEmail : handleReset}>
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Box display="inline-block" lineHeight={0}>
            <BrandLogo variant="full" h={{ base: '48px', sm: '56px' }} maxW="260px" />
          </Box>
          <Heading
            as="h1"
            fontSize={{ base: '22px', sm: '24px' }}
            fontWeight="700"
            color="text.primary"
            letterSpacing="-0.02em"
            mt={5}
          >
            Forgot password
          </Heading>
          <Text color="text.secondary" mt={2} fontSize="14px" lineHeight="1.5">
            {step === 'email'
              ? 'Enter your staff email. If it is registered, we will send an OTP.'
              : `Enter the OTP sent to ${email.trim()} and choose a new password.`}
          </Text>
        </Box>

        <VStack spacing={4} align="stretch">
          {step === 'email' ? (
            <FormControl isInvalid={Boolean(error) && !email.trim()}>
              <FormLabel fontSize="13px" fontWeight="600" mb={1.5}>
                Email
              </FormLabel>
              <Input
                type="email"
                placeholder="admin@utkarsh.com"
                h="52px"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
              />
            </FormControl>
          ) : (
            <>
              <FormControl isInvalid={!!errors.otp}>
                <FormLabel fontSize="13px" fontWeight="600" mb={1.5}>
                  OTP
                </FormLabel>
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit code"
                  h="52px"
                  letterSpacing="0.12em"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setErrors((prev) => {
                      const n = { ...prev };
                      delete n.otp;
                      return n;
                    });
                  }}
                  autoComplete="one-time-code"
                />
                {errors.otp ? (
                  <Text color="status.error" fontSize="12px" mt={1}>
                    {errors.otp}
                  </Text>
                ) : null}
              </FormControl>
              <AppPasswordInput
                label="New password"
                isRequired
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.password;
                    return n;
                  });
                }}
                placeholder={`Min ${STAFF_PASSWORD_MIN_LENGTH} characters`}
                isInvalid={!!errors.password}
                errorMessage={errors.password}
                autoComplete="new-password"
              />
              <AppPasswordInput
                label="Confirm password"
                isRequired
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrors((prev) => {
                    const n = { ...prev };
                    delete n.confirmPassword;
                    return n;
                  });
                }}
                isInvalid={!!errors.confirmPassword}
                errorMessage={errors.confirmPassword}
                autoComplete="new-password"
              />
            </>
          )}

          {error ? (
            <Text color="status.error" fontSize="13px" fontWeight="500">
              {error}
            </Text>
          ) : null}
        </VStack>

        <PrimaryButton type="submit" w="100%" isLoading={submitting} size="md">
          {step === 'email' ? 'Send OTP' : 'Update password'}
        </PrimaryButton>

        {step === 'reset' ? (
          <OutlineButton
            type="button"
            w="100%"
            size="md"
            onClick={handleResend}
            isDisabled={submitting}
          >
            Resend OTP
          </OutlineButton>
        ) : null}

        <Link
          as={RouterLink}
          to="/auth/sign-in"
          textAlign="center"
          fontSize="13px"
          fontWeight="600"
          color="text.secondary"
          _hover={{ color: 'text.primary', textDecoration: 'underline' }}
        >
          Back to sign in
        </Link>
      </VStack>
    </Box>
  );
}
